import { EventTransactionDTO } from './event-transaction';
// eslint-disable-next-line import/no-cycle
import { TicketSummaryDTO } from './order';
import { PaymentMethod } from './payment';
import { ShowtimeDTO } from './showtime';
import TicketDTO from './ticket';
import UserDTO from './user';
import VenueDTO from './venue';
import { WireTransferDTO } from './wire-transfer';
import { ListDTO } from './list';

export default interface EventDTO {
  id: string,
  business_id: string,
  name: string,
  description: string,
  date: string,
  ends_at: string,
  place: string,
  active: boolean,
  terms: string,
  phone: string,
  tickets: TicketDTO[]
  banner_image: string;
  maps_link: string;
  allow_mercadopago_payments: boolean;
  allow_payway_payments: boolean;
  allow_uala_payments: boolean;
  allow_nave_payments: boolean;
  allow_zilo_payments: boolean;
  allow_manual_payments: boolean;
  allow_in_person_sales: boolean;
  online_service_charge: number;
  manual_service_charge: number;
  min_age: number;
  address: string;
  require_id_tickets: boolean;
  receives_wire_transfers: boolean;
  has_polls: boolean;
  is_public: boolean;
  has_numerated_seats: boolean;
  tickets_includes_service_charge: boolean;
  has_seller_qr: boolean;
  has_scanner_auto_accept: boolean;
  has_order_qrs: boolean;
  is_important: boolean;
  is_approved: boolean;
  public_display: boolean;
  banner_display: boolean;
  public_status: EventPublicStatus;
  seller_sales_enabled: boolean;
  slug: string;
  seller_can_download_tickets: boolean;
  venue?: VenueDTO;
  custom_ticket_label: string | null;
  created_at: string;
  updated_at: string;
  has_audience_discounts: boolean;
  has_open_price: boolean;
  has_simplified_selling: boolean;
  has_showtimes: boolean;
  showtimes?: ShowtimeDTO[];
  has_subscription: boolean;
  notify_purchases: boolean;
  is_sold_out: boolean;
  business_name: string;
  superfast_checkout: boolean;
  has_order_referrals: 'enabled' | 'disabled' | 'enabled_unaccepted';
  meta_pixel_id: string | null;
  show_sellers_ranking: boolean;
  absorbed_service_charge_rate: number;
  online_comission_rate: number;
  has_lists: boolean;
  list?: ListDTO;
  order_phone_number: boolean;
  coproducer_reports: boolean;
  coproducer_balance: boolean;
}

export interface CategoryDTO {
  id: string;
  name: string;
  code: string;
  description: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}
export interface EventCategoryDTO {
  id: string;
  event_id: string;
  category_id: string;
  created_at: string;
  updated_at: string;
  event: EventDTO;
  category: CategoryDTO;
}

export interface SellerEventQR {
  qr_code: string;
  qr_image_b64: string;
}

export interface EventSalesReportDTO {
  total_amount_manual: number;
  total_amount_online: number;
  total_amount: number;
  tickets_count_manual: number;
  tickets_count_online: number;
  total_tickets_count: number;
  ticket_summary: TicketSummaryDTO[];
  total_manual_service_charge: number;
  total_amount_manual_with_service_charge: number;
  absorbed_service_charge_amount: number;
  sellers_manual_comission_amount: number;
  sellers_referral_comission_amount: number;
  total_comission_amount: number;
  net_amount: number;
  total_discounts: number;
  discounts_count: number;
  order_referral_comission_amount: number;
  sales_channel_share: {
    online: number;
    referral: number;
    seller: number;
  };
  comissions_share: {
    avantti: number;
    referral: number;
    seller: number;
    order_referral: number;
    absorbed_service_charge: number;
  };
}

export interface EventSalesByUserDTO {
  user_id: string;
  username: string;
  items_sold: number;
}

export interface EventAssistanceReportDTO {
  average_order_amount: number;
  average_tickets_per_order: number;
  tickets_count: number;
  tickets_sold_count: number;
  free_tickets_count: number;
  total_tickets_scanned: number;
  asistance_share:{
    assited: number;
    not_assited: number;
  };
  sales_channel_share: {
    online: number;
    manual: number;
    in_person: number;
  };
  free_tickets_share: {
    free: number;
    not_free: number;
  };
  sold_tickets_share: {
    not_available: number;
    available: number;
  };
  total_tickets_taken_by_type: {
    ticket_id: string;
    ticket_name: string;
    amount_taken: number;
  }[];
  total_tickets_taken_by_channel: {
    avantti: number;
    referrals: number;
    seller: number;
  };
  non_attendance_rate_by_ticket: {
    ticket_id: string;
    ticket_name: string;
    rate: number;
  }[];
  sales_in_period: Record<string, number>;
  sales_by_hour: Record<string, number>;
  tickets_used_by_hour: Record<string, number>;
  sales_until_event_end: Record<string, number>;
}

export interface SellerReferralsReportDTO {
  total_referral_amount: number;
  total_seller_amount: number;
  total_comission_amount: number;
  net_seller_amount: number;
  best_sellers: {
    username: string;
    total_amount: number;
  }[];
  order_average_best_sellers: {
    username: string;
    average_order_amount: number;
  }[];
  best_referrals: {
    referral_code: string;
    total_amount: number;
  }[];
}

export interface EventBalanceDTO {
  total_processed: number;
  tickets_amount: number;
  total_discounts: number;
  net_total: number;
  avantti_revenue: number;
  avantti_manual_fees: number;
  avantti_online_fees: number;
  gateway_fees: number;
  total_in_business_accounts: number;
  total_transferred: number;
  total_balance: number;
  wires: WireTransferDTO[];
  avantti_gross_amount_online: number;
  gross_received_manual_amount: number;
  absorbed_service_charge_amount: number;
}

export enum EventPublicStatus {
  Ready = 'ready',
  PendingApproval = 'pending_approval',
  NoTicketsAvailable = 'no_tickets_available',
  Expired = 'expired',
  Inactive = 'inactive',
}

export interface EventCashierReportDTO {
  cashier_id: string;
  amount: number;
  total: number;
  transactions: EventTransactionDTO[];
  transactions_amount: number;
  cash_in_amount: number;
  cash_out_amount: number;
  cash_balance: number;
  balance: number;
  payment_method_summary: Record<PaymentMethod, number>;
}

export interface EventAudienceDiscountDTO {
  id: string;
  name: string;
  requirements: string;
  percentage: number;
  active: boolean;
}

export interface CreateEventAudienceDiscountDTO {
  name: string;
  requirements: string;
  percentage: number;
}

export interface UpdateEventAudienceDiscountDTO {
  id: string;
  event_id: string;
  name: string;
  requirements: string;
  percentage: number;
}

export interface EventCashRegisterDTO {
  id: string;
  event_id: string;
  cash_register_id: string;
  status: string;
  is_active: boolean;
  in_use_by: string;
  users_can_access_reports: boolean;
  cash_register_sessions: EventCashRegisterSessionDTO[];
}

export interface CreateEventCashRegisterDTO {
  cash_register_id: string;
  is_active: boolean;
  users_can_access_reports: boolean;
}

export interface UpdateEventCashRegisterDTO {
  id: string;
  users_can_access_reports: boolean;
}

export interface EventCashRegisterMovementDTO {
  id: string;
  session_id: string;
  type: string;
  amount: number;
  performed_by_user_id: string;
  created_at: string;
  performed_by_user?: {
    id: string;
    username: string;
    email: string;
  };
}

export interface CreateEventCashRegisterMovementDTO {
  type: string;
  amount: number;
}

export interface EventCashRegisterSessionDTO {
  id: string;
  event_cash_register_id: string;
  opened_at: string;
  closed_at: string;
  initial_balance: number;
  closing_balance: number;
  user: UserDTO;
}

export interface OpenCashRegisterResponseDTO {
  cash_register_session: EventCashRegisterSessionDTO;
  event_cash_register: EventCashRegisterDTO;
}

export interface EventSubscriptionDTO {
  id: string;
  subscription_event_id: string;
  subscribed_event_id: string;
}

export interface CreateEventSubscriptionDTO {
  subscribed_event_id: string;
}

export interface EventSummaryDTO {
  event_id: string;
  event_name: string;
  start_date: Date;
  end_date: Date;
  business_name: string;
  status: EventPublicStatus;
  total_money_processed: number;
  money_in_our_hands: number;
  money_in_their_hands: number;
  mp_fees: number;
  online_service_charge_percentage: number;
  manual_service_charge_percentage: number;
  total_service_charge: number;
  online_service_charge_total: number;
  manual_service_charge_total: number;
  liquidation_amount: number;
  payment_method: Record<PaymentMethod, number>;
  total_tickets_sold: number;
  last_sell_date: Date | null;
}

export interface EventStatsDTO {
  total_sales_amount: string;
  total_orders: string;
  total_tickets_sold: string;
  events: EventSummaryDTO[];
}
